import { MessageSchema } from "../../../realm/schema";
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import LottieView from "lottie-react-native";
import { Pressable, Text } from "react-native";
import { useWindowDimensions } from 'react-native';
import VoicePlayManager, {
  VoicePlayEvent,
  VoicePlayState,
} from "../../../utils/VoicePlayManager";
import { ContentRenderProps } from "../ContentRender";
import RNFS from "react-native-fs";
import { showToast } from "../../../components/Toast";
import { fixRemoteUrl } from "../../../../imsdk/api";

type ComponentProps = ContentRenderProps & { styles: any };
export type VoiceContentIntent = {
  play: (type: "Speaker" | "Receiver") => void;
};

const VoiceRender = forwardRef<VoiceContentIntent, ComponentProps>(
  (props, refs) => {
    const { width: screenWidth } = useWindowDimensions();
    const lottie = useRef<LottieView>(null);
    const content = fixRemoteUrl(props.item.content);
    const mid = props.item.mid;
    useEffect(() => {
      if (content) {
        // 只预加载当前组件的音频
        VoicePlayManager.preloadUrl(content);
      }
      const localPath = props.item.meta.upload_path ?? "";
      if (localPath.length) {
        VoicePlayManager.preloadUrl(localPath);
      }
    }, [content]);
    useEffect(() => {
      // 监听播放事件广播
      VoicePlayManager.addListener(handlePlayEvent);

      return () => {
        VoicePlayManager.removeListener(handlePlayEvent);
      };
    }, [mid]);

    useImperativeHandle(refs, () => ({
      play(type) {
        handlePress(type);
      },
    }));

    /**
     * 处理播放事件广播
     * 只有当广播的 mid 与当前组件的 mid 相同时才处理
     */
    const handlePlayEvent = (event: VoicePlayEvent) => {
      if (event.mid !== mid) {
        return;
      }

      if (event.state === VoicePlayState.Playing) {
        playAnim();
      } else if (
        event.state === VoicePlayState.Ended ||
        event.state === VoicePlayState.Error
      ) {
        pauseAnim();
      }
    };

    /**
     * 播放动画
     */
    const playAnim = () => {
      lottie?.current?.reset();
      lottie?.current?.play();
    };

    /**
     * 暂停动画
     */
    const pauseAnim = () => {
      lottie?.current?.reset();
    };
    /**
     * 处理点击播放事件
     */
    async function handlePress(
      type: "Speaker" | "Receiver" | "Default" = "Default"
    ) {
      if (props.talkSelectStatus === true || props.disabled == true) {
        props.onClick?.();
        return;
      }

      try {
        let playUrl = content;
        const localPath = props.item.meta.upload_path ?? "";
        if (localPath.length) {
          const isExists = await RNFS.exists(localPath);
          if (isExists) {
            playUrl = localPath;
          }
        }
        // 通过单例管理器播放，自动处理停止逻辑和动画广播
        VoicePlayManager.play(mid, playUrl, type);
      } catch (error) {
        showToast("play error");
      }
    }

    let duration = Number(props.item.meta?.duration || "1");
    duration = Math.min(
      duration > 1000
        ? parseInt(`${duration / 1000}`)
        : parseInt(`${duration}`),
      60
    );
    duration = Math.floor(duration);

    const source = props.isSelf
      ? require("../../../assets/lottie/send.json")
      : require("../../../assets/lottie/receive.json");
    const padding = Math.min(screenWidth * 0.2, duration * 10);
    return (
      <Pressable
        onPress={() => handlePress()}
        onLongPress={props.onLongPress}
        disabled={props.talkSelectStatus === true}
        style={{
          alignItems: "center",
          justifyContent: "center",
          flexDirection: props.isSelf ? "row-reverse" : "row",
          minHeight: 40, // 确保最小高度与文本一致
        }}
      >
        <LottieView
          ref={lottie}
          style={{
            height: 18,
            width: 20,
            marginEnd: props.isSelf ? 10 : 5,
            marginStart: props.isSelf ? 5 : 10,
            marginVertical: 10,
            alignSelf: 'center', // 垂直居中
          }}
          loop
          source={source}
          imageAssetsFolder={"lottie/images"}
          autoPlay={false}
          resizeMode={"contain"}
        />
        <Text
          style={[
            props.styles,
            {
              paddingLeft: props.isSelf ? Math.max(padding, 12) : 2,
              paddingRight: props.isSelf ? 2 : Math.max(padding, 12),
            },
          ]}
        >
          {`${duration}″`}
        </Text>
      </Pressable>
    );
  }
);

export const VoiceContentRender = memo(VoiceRender, (prevProps, nextProps) => {
  return prevProps === nextProps;
});
